import PopupBasic from 'src/game/components/popups/PopupBasic';
import View from '@play-co/timestep-core/lib/ui/View';
import ScrollView from '@play-co/timestep-core/lib/ui/ScrollView';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import GCInstant from '@play-co/gcinstant';
import { PopupID, popupRegistry } from './popupRegistry';
import { duration } from 'src/replicant/utils/duration';
import StateObserver from 'src/StateObserver';

export default class PopupTestPopups extends PopupBasic {
  private container: ScrollView;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,

      zIndex: 0,
      titleBannerType: 'default',
      width: 600,
      height: 960,

      tracking: { feature: 'cheats', $subFeature: 'popup-test-popups' },
    });

    this.container = new ScrollView({
      superview: this.box,
      x: 10,
      y: 60,
      width: this.box.style.width - 48,
      height: this.box.style.height - 95,
      scrollX: false,
      scrollY: true,
    });

    this.addButtons();
  }

  private addButtons() {
    const columns = 2;
    const left = 14;
    const top = 34;
    const width = Math.floor(520 / columns);
    const height = 52;
    let currentLetter = '';

    POPUPS.forEach((popup, key) => {
      let x = left + width / 2 + width * (key % columns);
      const y = key > 1 ? top + height * Math.floor(key / columns) : top;
      if (!process.env.REPLICANT_OFFLINE) x += 25;

      if (
        currentLetter !== popup.name.charAt(0).toUpperCase() &&
        !(key % columns)
      ) {
        currentLetter = popup.name.charAt(0).toUpperCase();
        new LangBitmapFontTextView({
          superview: this.container,
          x: 24,
          y,
          width: 30,
          height,
          align: 'center',
          verticalAlign: 'center',
          size: 20,
          color: 'white',
          wordWrap: false,
          font: bitmapFonts('Title'),
          centerOnOrigin: true,
          localeText: () => currentLetter,
        });
      }

      new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,
        superview: this.container,
        localeText: () => popup.name,
        fontSize: 18,
        font: bitmapFonts('Title'),
        x,
        y,
        width: process.env.REPLICANT_OFFLINE ? width - 50 : width - 15,
        height,
        centerOnOrigin: true,
        onClick: async () => this.open(popup),
        tracking: { feature: 'cheats', $subFeature: 'popup-test-popups-open' },
      });

      if (process.env.REPLICANT_OFFLINE) {
        new ButtonScaleViewWithText({
          ...uiConfig.buttons.cancel,
          superview: this.container,
          localeText: () => 'GET',
          fontSize: 18,
          font: bitmapFonts('Title'),
          x: x + width / 2 - 10,
          y,
          width: 50,
          height,
          centerOnOrigin: true,
          onClick: async () => {
            window.location.href = `${window.location.protocol}//${window.location.host}${window.location.pathname}?showPopup=${popup.id}`;
          },
          tracking: {
            feature: 'cheats',
            $subFeature: 'popup-test-popups-restart',
          },
        });
      }
    });

    this.container.updateOpts({
      scrollBounds: {
        minY: 0,
        maxY: top + height * Math.ceil(Object.keys(POPUPS).length / columns),
      },
    });
  }

  private open(popup) {
    openPopupPromise(popup.id as PopupID, popup.params).catch((err) =>
      openPopupPromise('popupError' as PopupID, { message: err }),
    );
  }

  init(opts: {}) {
    super.init({});
    // update popup text
    this.title.setText(() => 'Popups');
  }
}

// We only add here popups that requires parameters
const popupParams = {
  popupError: { message: 'This is error message' },
  popupReplicationError: { message: 'This is the replicant error' },
  popupProfileAvatarSelect: {
    selected: GCInstant.playerPhoto,
  },
  popupGiftsConfirmationMulti: {
    avatars: new Array<string>(10)
      .fill('')
      .map((_, i) => `https://placekitten.com/200/200?i=${i}`),
  },
  popupConfirmInviteMulti: {
    avatars: new Array<string>(10)
      .fill('')
      .map((_, i) => `https://placekitten.com/200/200?i=${i}`),
  },
  popupVisitFriend: {
    friends: [
      {
        id: '1',
        name: 'Friend 1',
        photo: 'assets/avatar/1.png',
        species: 'ball',
        lastLogin: Date.now(),
      },
      {
        id: '2',
        name: 'Friend 2',
        photo: 'assets/avatar/2.png',
        species: 'crab',
        lastLogin: Date.now() - duration({ hours: 1 }),
      },
      {
        id: '3',
        name: 'Friend 3',
        photo: 'assets/avatar/3.png',
        species: 'dog',
        lastLogin: Date.now() - duration({ days: 1 }),
      },
      {
        id: '4',
        name: 'Friend 4',
        photo: 'assets/avatar/4.png',
        species: 'crab',
        lastLogin: Date.now() - duration({ days: 2 }),
      },
      {
        id: '5',
        name: 'Friend 5',
        photo: 'assets/avatar/5.png',
        species: 'dog',
        lastLogin: Date.now() - duration({ days: 7 }),
      },
    ],
    visitors: [
      {
        id: '1',
        name: 'Visitor 1',
        photo: 'assets/avatar/10.png',
        species: 'crab',
        lastVisit: Date.now(),
      },
      {
        id: '2',
        name: 'Visitor 2',
        photo: 'assets/avatar/11.png',
        species: 'ball',
        lastVisit: Date.now() - duration({ days: 4 }),
      },
    ],
  },
  popupConfirmVisitFriend: {
    id: '1',
    name: 'Test McTestFace',
    photo: '',
    lastLogin: Date.now() - duration({ days: 2 }),
    visits: 12,
    visited: 34,
    species: 'dog',
    petName: 'Doggo',
    age: 69,
    stomach: 69,
    love: 42,
  },
  popupPurchaseComplete: {
    old: { freeGems: 111, paidGems: 222 },
    new: { freeGems: 123, paidGems: 456 },
  },

  popupDuoResponse: {
    profile: {
      name: 'John Doe',
      photo: 'assets/profile/profile_1.png',
      visits: 42,
      visited: 12,
      lastLogin: Date.now() - duration({ hours: 2 }),
    },
    pet: {
      name: 'Pet Doe',
      species: 'giraffe',
      age: 1,
    },
    invitation: {
      item: 'i_ultimateVowsCoffee',
      timestamp: Date.now() - duration({ hours: 3, minutes: 15 }),
    },
  },

  popupDuoReminderConfirmation: {
    profile: {
      name: 'John Doe',
      photo: 'assets/profile/profile_1.png',
      visits: 42,
      visited: 12,
      lastLogin: Date.now() - duration({ hours: 2 }),
    },
    pet: {
      name: 'Pet Doe',
      species: 'giraffe',
      age: 1,
    },
    invitation: {
      item: 'i_ultimateVowsCoffee',
      timestamp: Date.now() - duration({ hours: 3, minutes: 15 }),
    },
  },

  popupInviteFriends: {
    friends: [
      {
        id: '1',
        name: 'Friend 1',
        photo: 'assets/avatar/1.png',
        species: 'ball',
        lastLogin: Date.now(),
      },
      {
        id: '2',
        name: 'Friend 2',
        photo: 'assets/avatar/2.png',
        species: 'crab',
        lastLogin: Date.now() - duration({ hours: 1 }),
      },
    ],
    subFeature: 'invite_icon',
    // subFeature: 'invite_no_energy',
  },
};

for (const popupID in popupParams) {
  const tmpParams = popupParams[popupID];
  delete popupParams[popupID];
  popupParams[popupID.toLowerCase()] = tmpParams;
}

const POPUPS = [];
const popupIDs = {};
for (const popupID in popupRegistry) {
  if (popupID === 'popupTestPopups') continue;
  const id = popupID.toLowerCase();
  popupIDs[id] = popupID;
  if (!popupParams[id]) popupParams[id] = {};
}

for (const popupID in popupParams) {
  POPUPS.push({
    id: popupIDs[popupID],
    params: popupParams[popupID],
    name: (popupIDs[popupID] || '').replace('popup', '').replace('Popup', ''),
  });
}

POPUPS.sort((a, b) =>
  a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
);

const url = new URL(window.location.href);
const popupID = url.searchParams.get('showPopup');
if (popupID) {
  // Wait StateObserver to be initialized before opening the pre-defined popup shortcut
  const interval = setInterval(() => {
    if (!StateObserver.invoke) return;
    clearInterval(interval);
    const id = popupID.toLocaleLowerCase();
    popupParams[id]
      ? void openPopupPromise(popupIDs[id], popupParams[id])
      : void openPopupPromise('popupError', {
          message: `${popupID}\nnot found.`,
        });
  }, 100);
}
